import { useState, useEffect, useRef } from "react";
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser as deleteUserApi,
  bulkImportUsers,
  getDepartments,
} from "../api/UsersApi";
import CulsightPageLoader from "../components/ui/CulsightLoader";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Search, Users as UsersIcon, Shield, AlertTriangle,
  TrendingUp, Plus, Upload, Edit, Trash2, Phone,
  ChevronLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const emptyUser = {
  email: "", name: "", department: "General", role: "viewer", phone: "", password: ""
};
const SEARCH_KEY = "search";
const getErrorMessage = (err, fallback) => {
  const data = err.response?.data;
  const detail = data?.detail ?? data?.message ?? data?.error;

  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        const field = Array.isArray(d.loc) ? d.loc[d.loc.length - 1] : "";
        return field ? `${field}: ${d.msg}` : d.msg || JSON.stringify(d);
      })
      .join(", ");
  }

  if (detail) return JSON.stringify(detail);
  return err.message || fallback; // response hi nahi aaya (network error etc.)
};

const Users = () => {
  const [users, setUsers] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnce = useRef(false);
  const requestIdRef = useRef(0);
  const [search, setSearch] = useState("");
  const isFirstRender = useRef(true);
  const [filterDept, setFilterDept] = useState("all");
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyUser);
  const [importOpen, setImportOpen] = useState(false);
  const [csvText, setCsvText] = useState("");
  const [defaultPw, setDefaultPw] = useState("Welcome123");
  const [importBusy, setImportBusy] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [stats, setStats] = useState({ total_users: 0, average_score: 0, high_risk: 0, low_risk: 0 });

  const deptScrollRef = useRef(null);

  const scrollDepartments = (direction) => {
    if (deptScrollRef.current) {
      deptScrollRef.current.scrollBy({ left: direction === "left" ? -150 : 150, behavior: "smooth" });
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const t = setTimeout(() => fetchUsers(filterDept, search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchUsers = async (dept = filterDept, q = search) => {
    const myRequestId = ++requestIdRef.current;

    if (!hasLoadedOnce.current) setInitialLoading(true);
    else setRefreshing(true);

    const startTime = Date.now();
    try {
      const extra = q ? { [SEARCH_KEY]: q } : {};
      const res = await getUsers(dept, extra);

      if (myRequestId === requestIdRef.current) {
        setUsers(res.data.results);
        setStats(res.data.summary);
      }
    } catch (e) {
      if (myRequestId === requestIdRef.current) {
        toast.error(getErrorMessage(e, "Failed to load users"));
      }
    }

    // spinner kam se kam 300ms dikhe, warna flicker bahut fast lagega
    const elapsed = Date.now() - startTime;
    const minDelay = 300;
    if (elapsed < minDelay) {
      await new Promise((resolve) => setTimeout(resolve, minDelay - elapsed));
    }

    if (myRequestId === requestIdRef.current) {
      hasLoadedOnce.current = true;
      setInitialLoading(false);
      setRefreshing(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await getDepartments();
      const list = res.data.results || res.data || [];
      setDepartments(list.map((d) => d.name));
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load departments"));
    }
  };

  const openCreate = () => { setEditingId(null); setForm(emptyUser); setUserDialogOpen(true); };
  const openEdit = (u) => {
    setEditingId(u.id);
    setForm({ email: u.email, name: u.name, department: u.department, role: u.role, phone: u.phone || "", password: "" });
    setUserDialogOpen(true);
  };

  const submitUser = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        const payload = { name: form.name, department: form.department, role: form.role, phone: form.phone };
        await updateUser(editingId, payload);
        toast.success("User updated");
      } else {
        await createUser(form);
        toast.success("User created");
      }
      setUserDialogOpen(false);
      setForm(emptyUser);
      setEditingId(null);
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to save user"));
    }
  };

  const openDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteUserApi(userToDelete.id);

      toast.success("User deleted");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete user"));
    }
  };

  // CSV import: parse header + rows. Supported headers: email,name,department,role,phone
  const parseCsv = (text) => {
    const lines = text.trim().split(/\r?\n/).filter(Boolean);
    if (lines.length === 0) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    return lines.slice(1).map((line) => {
      const values = line.split(",").map((v) => v.trim());
      const obj = {};
      headers.forEach((h, i) => { obj[h] = values[i] || ""; });
      return obj;
    });
  };

  // Frontend check nahi: rows empty ho tab bhi API chalegi, error backend se aayega
  const submitImport = async (e) => {
    e.preventDefault();
    const rows = parseCsv(csvText);
    setImportBusy(true);
    try {
      const res = await bulkImportUsers({
        users: rows,
        default_password: defaultPw,
      });
      const { created, skipped, errors } = res.data;
      toast.success(`Imported ${created} user(s). ${skipped} skipped.`);
      if (errors && errors.length) {
        console.warn("Import errors:", errors);
      }
      setImportOpen(false);
      setCsvText("");
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Import failed"));
    }
    setImportBusy(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const csv = "email,name,department,role,phone\njohn@example.com,John Doe,IT,viewer,+15551234567\njane@example.com,Jane Smith,HR,viewer,+15559876543\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "users-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const getRiskColor = (score) => {
    if (score >= 700) return { color: "#1fa7a1", label: "Low Risk", bg: "bg-teal-50" };
    if (score >= 500) return { color: "#f59e0b", label: "Medium Risk", bg: "bg-amber-50" };
    if (score >= 350) return { color: "#f97316", label: "High Risk", bg: "bg-orange-50" };
    return { color: "#ef4444", label: "Critical", bg: "bg-red-50" };
  };

  // Search ab backend se hota hai, isliye client-side filter nahi
  const filteredUsers = Array.isArray(users) ? users : [];

  if (initialLoading) {
    return <CulsightPageLoader />;
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1f1f23] tracking-tight">Users</h1>
          <p className="text-gray-500 text-sm mt-1">Manage users, phone numbers and risk scores</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setImportOpen(true)} data-testid="bulk-import-btn">
            <Upload size={16} /> Bulk Import
          </Button>
          <Button onClick={openCreate} className="bg-[#8c3add] hover:bg-[#702eb0] text-white gap-2" data-testid="add-user-btn">
            <Plus size={18} /> Add User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Total Users", value: stats.total_users, icon: UsersIcon, color: "#8c3add" },
          { label: "Low Risk", value: stats.low_risk, icon: Shield, color: "#1fa7a1" },
          { label: "High Risk", value: stats.high_risk, icon: AlertTriangle, color: "#ef4444" },
          { label: "Avg Score", value: stats.average_score, icon: TrendingUp, color: "#f59e0b" },
        ].map((stat, index) => (
          <Card key={index} className="border border-gray-100 shadow-sm">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl" style={{ backgroundColor: `${stat.color}15` }}>
                <stat.icon size={20} style={{ color: stat.color }} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#1f1f23]">{stat.value}</p>
                <p className="text-xs text-gray-500">{stat.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 min-w-[400px] max-w-md shrink-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input placeholder="Search name,email,phone and dep.." className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="users-search" />
        </div>

        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant={filterDept === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => { setFilterDept("all"); fetchUsers("all", search); }}
            className={`shrink-0 ${filterDept === "all" ? "bg-[#8c3add] hover:bg-[#702eb0]" : ""}`}
          >
            All
          </Button>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 p-2 h-8 w-8"
            onClick={() => scrollDepartments("left")}
          >
            <ChevronLeft size={16} />
          </Button>

          <div
            ref={deptScrollRef}
            className="flex gap-2 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none" }}
          >
            {departments.map((dept) => (
              <Button
                key={dept}
                variant={filterDept === dept ? "default" : "outline"}
                size="sm"
                onClick={() => { setFilterDept(dept); fetchUsers(dept, search); }}
                className={`shrink-0 ${filterDept === dept ? "bg-[#8c3add] hover:bg-[#702eb0]" : ""}`}
              >
                {dept}
              </Button>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 p-2 h-8 w-8"
            onClick={() => scrollDepartments("right")}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
      </div>

      {/* Users Grid */}
      <div className="relative">
        {refreshing && (
          <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-10 rounded-xl">
            <CulsightPageLoader />
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="users-grid">
          {filteredUsers.length === 0 ? (
            <div className="col-span-full py-12 text-center text-gray-500">
              No users found
            </div>
          ) : (
            filteredUsers.map((u) => {
              const risk = getRiskColor(u.risk_score);
              return (
                <Card key={u.id} className="border border-gray-100 shadow-sm card-hover" data-testid={`user-card-${u.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12 border-2" style={{ borderColor: risk.color }}>
                        <AvatarFallback className="bg-[#8c3add]/10 text-[#8c3add] font-semibold">
                          {u.name.split(" ").map((n) => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-[#1f1f23] truncate">{u.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{u.email}</p>
                        {u.phone && (
                          <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                            <Phone size={12} className="text-gray-400" /> {u.phone}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{u.department}</Badge>
                          <Badge variant="outline" className="text-xs capitalize">{u.role}</Badge>
                        </div>
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button variant="ghost" size="sm" className="p-1 h-7 w-7 hover:bg-[#1fa7a1]/10" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                          <Edit size={14} className="text-[#1fa7a1]" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-7 w-7 hover:bg-red-50"
                          onClick={() => openDeleteDialog(u)}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 size={14} className="text-red-500" />
                        </Button>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-gray-500">Risk Score</span>
                        <span className="text-lg font-bold" style={{ color: risk.color }}>{u.risk_score}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="h-2 rounded-full transition-all duration-500" style={{ width: `${(u.risk_score / 850) * 100}%`, backgroundColor: risk.color }} />
                      </div>
                      <div className="flex items-center justify-between mt-3 text-xs">
                        <span className={`px-2 py-1 rounded-full ${risk.bg}`} style={{ color: risk.color }}>{risk.label}</span>
                        <span className="text-gray-400">Susceptibility: {(u.phishing_susceptibility * 100).toFixed(0)}%</span>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-center">
                      <div className="p-2 rounded-lg bg-gray-50">
                        <p className="text-lg font-semibold text-[#1fa7a1]">{(u.training_completion * 100).toFixed(0)}%</p>
                        <p className="text-xs text-gray-500">Training</p>
                      </div>
                      <div className="p-2 rounded-lg bg-gray-50">
                        <p className="text-lg font-semibold text-[#8c3add]">{(u.reporting_rate * 100).toFixed(0)}%</p>
                        <p className="text-xs text-gray-500">Reporting</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={(o) => { setUserDialogOpen(o); if (!o) { setForm(emptyUser); setEditingId(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit User" : "Add User"}</DialogTitle>
          </DialogHeader>
          {/* noValidate: browser ki validation band, validation backend karega */}
          <form onSubmit={submitUser} noValidate className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Email {editingId && <span className="text-xs text-gray-400">(read-only)</span>}</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editingId} data-testid="user-email-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Phone (E.164 e.g. +15551234567)</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+15551234567" data-testid="user-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Department</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="user-department-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="viewer">Viewer</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    {/* <SelectItem value="admin">Admin</SelectItem> */}
                  </SelectContent>
                </Select>
              </div>
              {!editingId && (
                <div className="space-y-2">
                  <Label>Initial Password</Label>
                  <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Welcome123" data-testid="user-password-input" />
                </div>
              )}
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#8c3add] hover:bg-[#702eb0]" data-testid="user-submit-btn">
                {editingId ? "Save Changes" : "Create User"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Bulk Import Dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Users (CSV)</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitImport} noValidate className="space-y-3 mt-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                Expected columns: <code className="bg-white px-1 rounded">email, name, department, role, phone</code>
              </p>
              <Button type="button" variant="link" size="sm" onClick={downloadTemplate} className="text-[#8c3add]">
                Download template
              </Button>
            </div>
            <div className="space-y-2">
              <Label>Upload .csv file</Label>
              <Input type="file" accept=".csv,text/csv" onChange={handleFileUpload} data-testid="csv-file-input" />
            </div>
            <div className="space-y-2">
              <Label>or Paste CSV here</Label>
              <Textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                placeholder="email,name,department,role,phone&#10;john@example.com,John Doe,IT,viewer,+15551234567"
                rows={8}
                className="font-mono text-xs"
                data-testid="csv-paste-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Default password for imported users</Label>
              <Input value={defaultPw} onChange={(e) => setDefaultPw(e.target.value)} data-testid="default-pw-input" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setImportOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={importBusy} className="bg-[#8c3add] hover:bg-[#702eb0]" data-testid="import-submit-btn">
                {importBusy ? "Importing..." : "Import"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onOpenChange={(open) => {
          setDeleteDialogOpen(open);

          if (!open) {
            setUserToDelete(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>

          <div className="py-2">
            <p className="text-gray-600">
              Are you sure you want to delete{" "}
              <strong>{userToDelete?.name}</strong>?
            </p>

            <p className="text-sm text-gray-400 mt-2">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setUserToDelete(null);
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="destructive"
              onClick={handleDeleteUser}
            >
              <Trash2 size={16} className="mr-2" />
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;